/**
 * The three-region engineering workspace.
 *
 * Input remains on the left, visual verification in the centre and results on
 * the right on every viewport. Side-panel widths are percentages of the full
 * workspace and can be changed by dragging their inner edge separators.
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';

import { PANEL_LIMITS, clampLeft, clampRight } from './preferences';

interface WorkspaceShellProps {
  left: ReactNode;
  centre: ReactNode;
  right: ReactNode;
  leftWidth: number;
  rightWidth: number;
  leftVisible: boolean;
  rightVisible: boolean;
  onLeftWidthChange: (width: number) => void;
  onRightWidthChange: (width: number) => void;
}

export function WorkspaceShell({
  left, centre, right,
  leftWidth, rightWidth,
  leftVisible, rightVisible,
  onLeftWidthChange, onRightWidthChange,
}: WorkspaceShellProps) {
  const rowRef = useRef<HTMLDivElement>(null);

  return (
    <div className="workspace" ref={rowRef}>
      {leftVisible && (
        <>
          <div
            className="workspace-column"
            data-workspace-region="input"
            style={{ width: `${leftWidth}%`, flex: `0 0 ${leftWidth}%` }}
          >
            {left}
          </div>
          <ResizeHandle
            label="Resize the input panel"
            orientation="left"
            value={leftWidth}
            min={PANEL_LIMITS.leftMin}
            max={PANEL_LIMITS.leftMax}
            containerRef={rowRef}
            onChange={onLeftWidthChange}
          />
        </>
      )}

      <div className="workspace-column workspace-centre" data-workspace-region="graphics">{centre}</div>

      {rightVisible && (
        <>
          <ResizeHandle
            label="Resize the results panel"
            orientation="right"
            value={rightWidth}
            min={PANEL_LIMITS.rightMin}
            max={PANEL_LIMITS.rightMax}
            containerRef={rowRef}
            onChange={onRightWidthChange}
          />
          <div
            className="workspace-column"
            data-workspace-region="results"
            style={{ width: `${rightWidth}%`, flex: `0 0 ${rightWidth}%` }}
          >
            {right}
          </div>
        </>
      )}
    </div>
  );
}

function ResizeHandle({ label, orientation, value, min, max, containerRef, onChange }: {
  label: string;
  orientation: 'left' | 'right';
  value: number;
  min: number;
  max: number;
  containerRef: React.RefObject<HTMLDivElement>;
  onChange: (width: number) => void;
}) {
  const draggingRef = useRef(false);
  const clamp = orientation === 'left' ? clampLeft : clampRight;

  const handlePointerMove = useCallback((event: PointerEvent) => {
    if (!draggingRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    const bounds = container.getBoundingClientRect();
    if (bounds.width <= 0) return;
    const widthPixels = orientation === 'left'
      ? event.clientX - bounds.left
      : bounds.right - event.clientX;
    const widthPercent = (widthPixels / bounds.width) * 100;
    onChange(clamp(widthPercent));
  }, [clamp, containerRef, onChange, orientation]);

  const stopDragging = useCallback(() => {
    draggingRef.current = false;
    document.body.classList.remove('is-resizing');
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
      document.body.classList.remove('is-resizing');
    };
  }, [handlePointerMove, stopDragging]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const step = event.shiftKey ? 2 : 0.5;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onChange(clamp(orientation === 'left' ? value - step : value + step));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onChange(clamp(orientation === 'left' ? value + step : value - step));
    } else if (event.key === 'Home') {
      event.preventDefault();
      onChange(min);
    } else if (event.key === 'End') {
      event.preventDefault();
      onChange(max);
    }
  };

  return (
    <div
      className="resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value * 10) / 10}
      aria-valuetext={`${value.toFixed(1)} percent`}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={(event) => {
        event.preventDefault();
        draggingRef.current = true;
        document.body.classList.add('is-resizing');
      }}
    />
  );
}
